import type {
	StatusDisplay2,
	StatusLifecycleDisplay,
	StatusTransitionDisplay,
} from "@/services/lifecycle/lifecycle.types";
import { isLifecycleActive } from "@/utils/lifecycle-admin";

export type LifecycleScope = "platform" | "organization";

export function normalizeLifecycleSource(row: StatusLifecycleDisplay): string {
	if (row.isDefault === true) return "system";
	if (row.isDefault === false) return "custom";
	return (row.source ?? "").trim().toLowerCase();
}

export function isSystemLifecycle(row: StatusLifecycleDisplay): boolean {
	if (row.isDefault === true) return true;
	if (row.isDefault === false) return false;
	const source = (row.source ?? "").trim().toLowerCase();
	return source === "" || source === "system";
}

export function isCustomLifecycle(row: StatusLifecycleDisplay): boolean {
	if (row.isDefault === true) return false;
	if (row.isDefault === false) return true;
	return (row.source ?? "").trim().toLowerCase() === "custom";
}

export function canShowActivityTab(
	scope: LifecycleScope,
	_row: StatusLifecycleDisplay,
): boolean {
	if (scope === "platform" || scope === "organization") return true;
	return false;
}

export function formatLifecycleVersion(row: StatusLifecycleDisplay): string {
	const maybeVersion = (row as Record<string, unknown>).version;
	if (typeof maybeVersion === "number" && Number.isFinite(maybeVersion)) {
		return maybeVersion.toFixed(1);
	}
	return "1.0";
}

export function truncateLifecycleId(id: string): string {
	if (id.length <= 12) return id;
	return `${id.slice(0, 6)}...${id.slice(-4)}`;
}

export function getLifecycleDisplayName(row: StatusLifecycleDisplay): string {
	return row.name?.trim() || "";
}

function statusSortValue(status: StatusDisplay2): number {
	const raw = status as Record<string, unknown>;
	const displayOrder = raw.displayOrder;
	if (typeof displayOrder === "number" && Number.isFinite(displayOrder))
		return displayOrder;
	const seq = raw.sequence;
	if (typeof seq === "number" && Number.isFinite(seq)) return seq;
	return Number.MAX_SAFE_INTEGER;
}

export function getLifecycleStatuses(
	row: StatusLifecycleDisplay,
): StatusDisplay2[] {
	const rawRow = row as Record<string, unknown>;

	// Prefer a top-level `statuses` array when the API returns one
	const statusesArray = rawRow.statuses;
	if (Array.isArray(statusesArray) && statusesArray.length > 0) {
		// Deduplicate by ID — the backend can occasionally return the same
		// status more than once (e.g. when initialStatus is also in the list)
		const seen = new Set<string>();
		const deduped = (statusesArray as StatusDisplay2[]).filter((s) => {
			if (!s.id || seen.has(s.id)) return false;
			seen.add(s.id);
			return true;
		});
		return deduped.sort((a, b) => {
			const diff =
				statusSortValue(a as StatusDisplay2) -
				statusSortValue(b as StatusDisplay2);
			if (diff !== 0) return diff;
			return ((a as StatusDisplay2).name ?? "").localeCompare(
				(b as StatusDisplay2).name ?? "",
			);
		});
	}

	// Fallback: collect unique statuses from transitions
	const map = new Map<string, StatusDisplay2>();
	const initial = row.initialStatus;
	if (initial?.id) map.set(initial.id, initial);

	const transitions = row.transitions ?? [];
	for (const transition of transitions) {
		if (transition.fromStatus?.id)
			map.set(transition.fromStatus.id, transition.fromStatus);
		if (transition.toStatus?.id)
			map.set(transition.toStatus.id, transition.toStatus);
	}

	return Array.from(map.values()).sort((a, b) => {
		const diff = statusSortValue(a) - statusSortValue(b);
		if (diff !== 0) return diff;
		return (a.name ?? "").localeCompare(b.name ?? "");
	});
}

export function getLifecycleTransitions(
	row: StatusLifecycleDisplay,
): StatusTransitionDisplay[] {
	return (row.transitions ?? []).filter(
		(transition) => transition.fromStatus || transition.toStatus,
	);
}

export function getLifecycleStateLabel(
	row: StatusLifecycleDisplay,
): "active" | "draft" {
	return isLifecycleActive(row) ? "active" : "draft";
}
